import { BaseBloc } from '@core/utils/bloc';
import { Repository } from '@core/utils/repository';
import { handleMessage, requestMessage } from '@business/messages';
import {
  ChecklistId,
  ChecklistResponse,
  CompactChecklistResponse,
  ChecklistStepResponse,
  ChecklistTemplateResponse,
  ChecklistTemplateId,
} from '@business/entities/checklist';
import { ChecklistAPI } from '@business/api/checklist';
import { UserId } from '@business/entities/account';
import { TeamId } from '@business/entities/team';
import { TodoChecklistAPI } from '@business/api/TodoChecklist';
import { showDeleteConfirmation } from '@modules/common';
import { switchMap } from 'rxjs/operators';

export class TodoChecklistBloc extends BaseBloc {
  private readonly checklistRepo = new Repository<ChecklistResponse | CompactChecklistResponse>({
    getItemId: checklist => checklist.id,
  });

  readonly checklists$ = this.checklistRepo.items$;
  readonly selectChecklist = this.checklistRepo.selectItem;

  onReset() {
    this.checklistRepo.reset();
  }

  fetchChecklists = (checklistId?: ChecklistId) => {
    return ChecklistAPI.fetchChecklists(checklistId).pipe(
      this.checklistRepo.ops.reset(),
      handleMessage({ error: requestMessage('fetch_templates_error') }),
    );
  };

  fetchChecklist = (id: ChecklistId) => {
    return ChecklistAPI.fetchChecklist(id).pipe(
      this.checklistRepo.ops.upsertOne(item => ({ item })),
      handleMessage({ error: requestMessage('fetch_templates_error') }),
    );
  };

  createChecklist = (checklist: Partial<ChecklistResponse>) => {
    return ChecklistAPI.createChecklist(checklist).pipe(
      this.checklistRepo.ops.addOne(item => item),
      handleMessage({
        type: requestMessage('create_checklist'),
      }),
    );
  };

  updateChecklist = (id: ChecklistTemplateId, data: Partial<ChecklistResponse>) => {
    return ChecklistAPI.updateChecklist(id, data).pipe(
      this.checklistRepo.ops.upsertOne(item => ({ item })),
      handleMessage({
        type: requestMessage('update_checklist'),
      }),
    );
  };

  toggleStep = (id: number, complete: boolean) => {
    return ChecklistAPI.toggleStep(id, complete).pipe(
      this.checklistRepo.ops.upsertOne(item => ({ item })),
      handleMessage({
        type: requestMessage('update_checklist'),
      }),
    );
  };

  search = (id: UserId | TeamId) => {
    return ChecklistAPI.search(id).pipe(this.checklistRepo.ops.reset(), handleMessage({ error: requestMessage('fetch_templates_error') }));
  };

  /* Sections */
  createChecklistSection = (ChecklistTemplateId: ChecklistId, name: string) => {
    return ChecklistAPI.createChecklistSection(ChecklistTemplateId, name).pipe(
      switchMap(() => this.fetchChecklist(ChecklistTemplateId)),
      handleMessage({
        type: requestMessage('create_checklist_gc_section'),
      }),
    );
  };

  updateChecklistSection = (ChecklistTemplateId: ChecklistId, id: number, name: string) => {
    return TodoChecklistAPI.updateChecklistSection(id, name).pipe(
      switchMap(() => this.fetchChecklist(ChecklistTemplateId)),
      handleMessage({
        type: requestMessage('update_checklist_gc_section'),
      }),
    );
  };

  deleteChecklistSection = (ChecklistTemplateId: ChecklistId, id: number) => {
    return showDeleteConfirmation('Delete Section', 'Do you really want to remove this Section?').pipe(
      switchMap(() => TodoChecklistAPI.deleteChecklistSection(id)),
      switchMap(() => this.fetchChecklist(ChecklistTemplateId)),
      handleMessage({
        type: requestMessage('delete_checklist_gc_section'),
      }),
    );
  };

  deleteChecklistStep = (ChecklistTemplateId: ChecklistId, id: number) => {
    return showDeleteConfirmation('Delete Step', 'Do you really want to remove this Step?').pipe(
      switchMap(() => ChecklistAPI.deleteChecklistStep(id)),
      switchMap(() => this.fetchChecklist(ChecklistTemplateId)),
      handleMessage({
        type: requestMessage('delete_checklist_gc_section_step'),
      }),
    );
  };

  createChecklistStep = (ChecklistTemplateId: ChecklistId, sectionId: number, data: Partial<ChecklistStepResponse>, parentStepId?: number) => {
    return ChecklistAPI.createChecklistStep(ChecklistTemplateId, sectionId, data, parentStepId).pipe(
      switchMap(() => this.fetchChecklist(ChecklistTemplateId)),
      handleMessage({
        type: requestMessage('create_checklist_gc_section_step'),
      }),
    );
  };

  updateAssignne = (type: string, id: number | string, items: string[]) => {
    return ChecklistAPI.updateAssignne(type, id, items).pipe(this.checklistRepo.ops.upsertOne(item => ({ item })));
  };

  updateChecklistStep = (ChecklistTemplateId: ChecklistId, id: number, data: Partial<ChecklistStepResponse>) => {
    return ChecklistAPI.updateChecklistStep(id, data).pipe(
      switchMap(() => this.fetchChecklist(ChecklistTemplateId)),
      handleMessage({
        type: requestMessage('update_checklist_gc_section_step'),
      }),
    );
  };
}
